/**
 * Route Analysis Data Models
 * Based on the Route Analysis API Documentation
 */

import { colors } from '../config/colors';

// Risk Level Enum
export const RISK_LEVELS = {
  LOW: 'LOW',
  MEDIUM: 'MEDIUM',
  HIGH: 'HIGH'
};

// Route Analysis DTO
export class RouteAnalysisDTO {
  constructor(data = {}) {
    // Identification
    this.routeId = data.route_id || '';
    this.routeName = data.route_name || '';
    this.description = data.description || '';
    
    // Location Details
    this.startAddress = data.start_address || '';
    this.startCity = data.start_city || '';
    this.startProvince = data.start_province || '';
    this.endAddress = data.end_address || '';
    this.endCity = data.end_city || '';
    this.endProvince = data.end_province || '';
    
    // Distance & Duration
    this.distanceKm = data.distance_km || 0;
    this.distanceText = data.distance_text || '';
    this.durationMinutes = data.duration_minutes || 0;
    this.durationText = data.duration_text || '';
    this.durationInTrafficMinutes = data.duration_in_traffic_minutes || null;
    this.durationInTrafficText = data.duration_in_traffic_text || null;
    
    // Coordinates for Mapping
    this.startLat = data.start_lat || 0;
    this.startLng = data.start_lng || 0;
    this.endLat = data.end_lat || 0;
    this.endLng = data.end_lng || 0;
    this.overviewPolyline = data.overview_polyline || '';
    
    // Risk Analysis
    this.riskScore = data.risk_score || 0;
    this.riskLevel = data.risk_level || RISK_LEVELS.LOW;
    this.riskFactors = data.risk_factors || [];
    
    // Status
    this.isActive = data.is_active !== undefined ? data.is_active : true;
    this.lastAnalyzed = data.last_analyzed || null;
    
    // Transportation
    this.carrier = data.carrier || '';
    this.vehicleType = data.vehicle_type || '';
    this.estimatedCost = data.estimated_cost || 0;
  }

  toJSON() {
    return {
      route_id: this.routeId,
      route_name: this.routeName,
      description: this.description,
      start_address: this.startAddress,
      start_city: this.startCity,
      start_province: this.startProvince,
      end_address: this.endAddress,
      end_city: this.endCity,
      end_province: this.endProvince,
      distance_km: this.distanceKm,
      distance_text: this.distanceText,
      duration_minutes: this.durationMinutes,
      duration_text: this.durationText,
      duration_in_traffic_minutes: this.durationInTrafficMinutes,
      duration_in_traffic_text: this.durationInTrafficText,
      start_lat: this.startLat,
      start_lng: this.startLng,
      end_lat: this.endLat,
      end_lng: this.endLng,
      overview_polyline: this.overviewPolyline,
      risk_score: this.riskScore,
      risk_level: this.riskLevel,
      risk_factors: this.riskFactors,
      is_active: this.isActive,
      last_analyzed: this.lastAnalyzed,
      carrier: this.carrier,
      vehicle_type: this.vehicleType,
      estimated_cost: this.estimatedCost
    };
  }

  // Helper method to get full start address
  getStartFullAddress() {
    const parts = [this.startAddress, this.startCity, this.startProvince].filter(p => p);
    return parts.join(', ');
  }

  // Helper method to get full end address
  getEndFullAddress() {
    const parts = [this.endAddress, this.endCity, this.endProvince].filter(p => p);
    return parts.join(', ');
  }

  // Check if analysis is stale (> 7 days old)
  isStale() {
    if (!this.lastAnalyzed) return true;
    const sevenDaysAgo = new Date();
    sevenDaysAgo.setDate(sevenDaysAgo.getDate() - 7);
    return new Date(this.lastAnalyzed) < sevenDaysAgo;
  }
}

// Helper Functions

/**
 * Get color styling for risk level
 */
export const getRiskLevelColor = (riskLevel) => {
  switch (riskLevel) {
    case RISK_LEVELS.LOW:
      return {
        bg: colors.success[100],
        text: colors.success[700],
        border: colors.success[300],
        progressBar: colors.success[500]
      };
    case RISK_LEVELS.MEDIUM:
      return {
        bg: colors.warning[100],
        text: colors.warning[700],
        border: colors.warning[300],
        progressBar: colors.warning[500]
      };
    case RISK_LEVELS.HIGH:
      return {
        bg: colors.danger[100],
        text: colors.danger[700],
        border: colors.danger[300],
        progressBar: colors.danger[500]
      };
    default:
      return {
        bg: colors.gray[100],
        text: colors.gray[700],
        border: colors.gray[300],
        progressBar: colors.gray[500]
      };
  }
};

/**
 * Get color for risk score (0-100)
 */
export const getRiskScoreColor = (score) => {
  if (score < 30) {
    return getRiskLevelColor(RISK_LEVELS.LOW);
  } else if (score < 60) {
    return getRiskLevelColor(RISK_LEVELS.MEDIUM);
  } else {
    return getRiskLevelColor(RISK_LEVELS.HIGH);
  }
};

/**
 * Format distance for display
 */
export const formatDistance = (distanceKm) => {
  if (!distanceKm) return 'N/A';
  return `${distanceKm.toFixed(1)} km`;
};

/**
 * Format duration for display
 */
export const formatDuration = (durationMinutes) => {
  if (!durationMinutes) return 'N/A';
  
  const hours = Math.floor(durationMinutes / 60);
  const minutes = Math.round(durationMinutes % 60);
  
  if (hours > 0) {
    return `${hours}h ${minutes}m`;
  }
  return `${minutes} mins`;
};

/**
 * Format cost for display
 */
export const formatCost = (cost, currency = 'CAD') => {
  if (!cost) return 'N/A';
  return `$${cost.toFixed(2)} ${currency}`;
};

/**
 * Format last analyzed timestamp
 */
export const formatLastAnalyzed = (timestamp) => {
  if (!timestamp) return 'Never analyzed';
  
  const date = new Date(timestamp);
  const now = new Date();
  const diffMs = now - date;
  const diffMins = Math.floor(diffMs / 60000);
  const diffHours = Math.floor(diffMs / 3600000);
  const diffDays = Math.floor(diffMs / 86400000);
  
  if (diffMins < 1) return 'Just now';
  if (diffMins < 60) return `${diffMins} minutes ago`;
  if (diffHours < 24) return `${diffHours} hours ago`;
  if (diffDays < 7) return `${diffDays} days ago`;
  
  return date.toLocaleDateString();
};

// Factory Functions
export const createRouteAnalysis = (data) => new RouteAnalysisDTO(data);

